import { useState, useEffect, useRef } from "react";
import "@/App.css";
import "@/SocialImageBuilder.css";
import "@/SocialVideoBuilder.css";
import "@/PresentourBuilder.css";
import "@/ModuleSelector.css";
import "@/AuthPage.css";
import "@/ScriptHistory.css";
import "@/AdminPanel.css";
import "@/EditorPanel.css";
import "@/ManagerPanel.css";
import "@/BranchListings.css";
import { AuthProvider, useAuth } from "@/AuthContext";
import AuthPage from "@/AuthPage";
import ShowcaseLanding from "@/ShowcaseLanding";
import PresenTourPage from "@/PresenTourPage";
import NarraTourPage from "@/NarraTourPage";
import SocialWeekPage from "@/SocialWeekPage";
import ClipSortPage from "@/ClipSortPage";
import NicheSocialLanding from "@/NicheSocialLanding";
import AvatarGuide from "@/AvatarGuide";
import RegionalDirectorDashboard from "@/RegionalDirectorDashboard";
import SocialManagerPortal from "@/SocialManagerPortal";
import ProposalPublicView from "@/ProposalPublicView";
import "@/LandingPage.css";
import CaptureApp from "@/CaptureApp";
import "@/CaptureApp.css";
import ModuleSelector from "@/ModuleSelector";
import ScriptBuilder from "@/ScriptBuilder";
import SocialImageBuilder from "@/SocialImageBuilder";
import SocialVideoBuilder from "@/SocialVideoBuilder";
import PresentourBuilder from "@/PresentourBuilder";
import AdminPanel from "@/AdminPanel";
import EditorPanel from "@/EditorPanel";
import ManagerPanel from "@/ManagerPanel";
import BranchListings from "@/BranchListings";
import VideoSearch from "@/VideoSearch";
import SocialLite from "@/SocialLite";
import CostCentre from "@/CostCentre";
import AgentReels from "@/AgentReels";
import SocialWeekBuilder from "@/SocialWeekBuilder";
import ValuationTools from "@/ValuationTools";
import PropertyDescriptionBuilder from "@/PropertyDescriptionBuilder";
import AvatarOnboardingBuilder from "@/AvatarOnboardingBuilder";
import EmploymentIntelligence from "@/EmploymentIntelligence";
import AutocueView from "@/AutocueView";
import TeamPage from "@/TeamPage";
import SSOLanding from "@/SSOLanding";
import JacksonStopsPresentation, { PRESENTATION_BASE } from "@/presentation/JacksonStopsPresentation";
import CarouselRender from "@/CarouselRender";
import ArtworkStudio from "@/ArtworkStudio";
import "@/SocialWeek.css";
import { Toaster } from "sonner";

import { LogOut } from "lucide-react";

// URL <-> activeModule mapping for authenticated pages.
// Keeps the address bar in sync so refresh, deep-linking and browser
// back/forward all work. Pure state-based navigation broke refresh.
const MODULE_TO_PATH = {
  'script': '/scripts',
  'proposals': '/proposals',
  'team': '/team',
  'social': '/social-image',
  'video': '/social-video',
  'presentour': '/presentour-builder',
  'admin': '/admin',
  'social-hub': '/social-hub',
  'management': '/management',
  'listings': '/listings',
  'reels': '/reels',
  'social-week': '/social-week',
  'valuation-tools': '/valuation-tools',
  'property-description': '/property-description',
  'avatar-onboarding': '/avatar-onboarding',
  'employment-intelligence': '/employment-intelligence',
  'editor': '/editor',
  'video-search': '/order-search',
  'social-lite': '/social-lite',
  'cost-centre': '/cost-centre',
};
const PATH_TO_MODULE = Object.fromEntries(
  Object.entries(MODULE_TO_PATH).map(([k, v]) => [v, k])
);
PATH_TO_MODULE['/video-search'] = 'video-search'; // legacy URL for Order Search

// Public (unauthenticated) landing sub-pages
const PUBLIC_TO_PATH = {
  'presentour': '/info/presentour',
  'narratour': '/info/narratour',
  'socialweek': '/info/social-week',
  'nichesocial': '/nichesocial',
  'avatar-guide': '/info/avatar-guide',
};
const PATH_TO_PUBLIC = Object.fromEntries(
  Object.entries(PUBLIC_TO_PATH).map(([k, v]) => [v, k])
);

function ImpersonationBanner() {
  const { user, impersonating, stopImpersonating } = useAuth();
  if (!impersonating) return null;
  return (
    <div data-testid="impersonation-banner" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#f59e0b', color: '#000', padding: '6px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 12, fontSize: 13, fontWeight: 600,
    }}>
      <span>Viewing as: {user?.name} ({user?.role}){user?.branch_name ? ` — ${user.branch_name}` : user?.client_name ? ` — ${user.client_name}` : ''}</span>
      <button data-testid="stop-impersonation-btn" onClick={stopImpersonating} style={{
        background: '#000', color: '#f59e0b', border: 'none', borderRadius: 6,
        padding: '4px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
        fontSize: 12, fontWeight: 700,
      }}><LogOut size={12} /> Back to Super Admin</button>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, loading, user, impersonating, sessionExpired } = useAuth();
  // Initialize activeModule from URL so refresh keeps you on the same page
  const [activeModule, setActiveModule] = useState(() => {
    if (typeof window === 'undefined') return null;
    return PATH_TO_MODULE[window.location.pathname] || null;
  });
  const [presentourScriptData, setPresentourScriptData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  // Expired session → straight back to the sign-in form (URL is kept so login returns here)
  useEffect(() => { if (sessionExpired && !isAuthenticated) setShowAuth(true); }, [sessionExpired, isAuthenticated]);
  const [publicPage, setPublicPage] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (window.location.pathname.startsWith('/clipsort/')) return 'clipsort';
    return PATH_TO_PUBLIC[window.location.pathname] || null;
  });
  // WMS single sign-on handoff (/sso?token=...) — exchange before showing anything else
  const [ssoPending, setSsoPending] = useState(() => typeof window !== 'undefined' && window.location.pathname === '/sso');

  // Reset to module selector when user changes (impersonation), but not on
  // initial login. Without the prevUserIdRef guard, hydrating auth on a
  // direct URL like /social-week wipes activeModule and sends user back to /.
  const prevUserIdRef = useRef(undefined);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    const curr = user?.id;
    // Reset only on switch between two real users (e.g. start/stop impersonation)
    if (prev !== undefined && prev !== curr && curr) {
      setActiveModule(null);
      setShowMenu(false);
    }
    prevUserIdRef.current = curr;
  }, [user?.id]);

  // Keep the URL in sync with the current module / public page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Don't touch standalone /capture or /artwork routes
    if (window.location.pathname === '/capture' || window.location.pathname === '/artwork') return;
    // Don't rewrite the client clip-sort link (slug lives in the path)
    if (window.location.pathname.startsWith('/clipsort/')) return;
    // Hold the /sso URL until the token exchange has finished
    if (ssoPending) return;
    // Wait for auth resolution to avoid bouncing the URL during hydration
    if (loading) return;
    let desired = '/';
    // /nichesocial and /info/avatar-guide are viewable for both auth states — prioritise them
    if (publicPage === 'nichesocial' || publicPage === 'avatar-guide' || publicPage === 'socialweek') {
      desired = PUBLIC_TO_PATH[publicPage];
    } else if (isAuthenticated && activeModule && MODULE_TO_PATH[activeModule]) {
      desired = MODULE_TO_PATH[activeModule];
    } else if (!isAuthenticated && publicPage && PUBLIC_TO_PATH[publicPage]) {
      desired = PUBLIC_TO_PATH[publicPage];
    }
    if (window.location.pathname !== desired) {
      window.history.pushState({}, '', desired);
    }
  }, [activeModule, publicPage, isAuthenticated, loading, ssoPending]);

  // Sync state when user uses browser back/forward
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const path = window.location.pathname;
      if (path === '/capture' || path === '/artwork') return;
      if (path.startsWith('/clipsort/')) { setPublicPage('clipsort'); return; }
      setActiveModule(PATH_TO_MODULE[path] || null);
      setPublicPage(PATH_TO_PUBLIC[path] || null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Guard: if URL directly points at /social-week but the user's branch
  // doesn't have Social Week access, bounce them to the NicheSocial pitch.
  useEffect(() => {
    if (!isAuthenticated || loading) return;
    if (activeModule === 'social-week' && user && user.role !== 'super_admin' && !user.social_week_access) {
      setActiveModule(null);
      setPublicPage('nichesocial');
      window.scrollTo(0, 0);
    }
  }, [activeModule, user, isAuthenticated, loading]);

  // Guard: Employment Intelligence is super-admin only. Bounce non-super-admins
  // back to the module selector if they hit /employment-intelligence directly.
  useEffect(() => {
    if (!isAuthenticated || loading) return;
    if (activeModule === 'employment-intelligence' && user && user.role !== 'super_admin') {
      setActiveModule(null);
      window.history.replaceState({}, '', '/');
      window.scrollTo(0, 0);
    }
  }, [activeModule, user, isAuthenticated, loading]);

  // Auto-redirect editors to the Editor Dashboard on login
  const role = user?.role || '';
  const isSuperAdmin = role === 'super_admin';
  const isClientManager = role === 'client_manager';
  const isEditor = role === 'editor';
  const isRD = role === 'regional_director' || role === 'bdm';
  const isManager = ['branch_manager'].includes(role);
  const isSocialManager = role === 'social_manager';

  // Header cog (super admin) asks App to open a module from anywhere
  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail === 'admin' && isSuperAdmin) handleSelectModule('admin');
      if (e.detail === 'script') handleSelectModule('script');
    };
    window.addEventListener('nichesocial:open-module', onOpen);
    return () => window.removeEventListener('nichesocial:open-module', onOpen);
  });

  const handleSelectModule = (module) => {
    // Social Week is a paid bolt-on. If the branch doesn't have access, route
    // them to the NicheSocial landing instead of opening the builder.
    if (module === 'social-week' && user && !isSuperAdmin && !user.social_week_access) {
      setActiveModule(null);
      setShowMenu(false);
      setPublicPage('nichesocial');
      window.scrollTo(0, 0);
      return;
    }
    setActiveModule(module);
    setShowMenu(false);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveModule(null);
    setShowMenu(!isEditor);
    setPresentourScriptData(null);
    window.scrollTo(0, 0);
  };

  const handleBuildPresentour = (sections) => {
    setPresentourScriptData({ sections });
    setActiveModule('presentour');
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner"></div>
      </div>
    );
  }

  // WMS → NicheSocial single sign-on landing
  if (ssoPending) {
    return (
      <SSOLanding
        onSuccess={(module) => {
          setActiveModule(module && MODULE_TO_PATH[module] ? module : null);
          setShowMenu(false);
          setSsoPending(false);
          window.scrollTo(0, 0);
        }}
        onFallbackLogin={() => {
          setSsoPending(false);
          window.history.replaceState({}, '', '/');
          setShowAuth(true);
        }}
      />
    );
  }

  // /artwork — super-admin studio for rendering the showcase roundabout as print PNGs
  if (window.location.pathname === '/artwork') {
    if (!isAuthenticated) return <AuthPage onBack={() => { window.location.href = '/'; }} />;
    return <ArtworkStudio user={user} />;
  }

  // /nichesocial is accessible to both authenticated and unauthenticated users.
  // Authenticated users land here when their branch doesn't have Social Week
  // access enabled — they can read the pitch and click "Back" to return.
  if (publicPage === 'nichesocial') {
    return (
      <NicheSocialLanding
        onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }}
        onLogin={!isAuthenticated ? () => setShowAuth(true) : undefined}
      />
    );
  }

  // Avatar guide is a shareable public page — accessible whether logged in or not.
  if (publicPage === 'avatar-guide') {
    return (
      <AvatarGuide onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }} />
    );
  }

  // Client clip-sort page — public, slug-based, accessible in both auth states.
  if (publicPage === 'clipsort') {
    return <ClipSortPage slug={window.location.pathname.split('/clipsort/')[1] || ''} />;
  }

  // Social Week info page — shareable, passcode protected, accessible in both auth states.
  if (publicPage === 'socialweek') {
    return (
      <SocialWeekPage
        onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }}
        onDemo={() => { setPublicPage(null); setTimeout(() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
      />
    );
  }

  // Show auth page if not logged in
  if (!isAuthenticated) {
    if (showAuth) {
      return <AuthPage onBack={() => setShowAuth(false)} />;
    }
    if (publicPage === 'presentour') {
      return <PresenTourPage onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }} onDemo={() => { setPublicPage(null); setTimeout(() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' }), 100); }} />;
    }
    if (publicPage === 'narratour') {
      return <NarraTourPage onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }} onDemo={() => { setPublicPage(null); setTimeout(() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' }), 100); }} />;
    }
    if (publicPage === 'avatar-guide') {
      return <AvatarGuide onBack={() => { setPublicPage(null); window.scrollTo(0, 0); }} />;
    }
    return <ShowcaseLanding onLogin={() => setShowAuth(true)} onNavigate={(page) => { setPublicPage(page); window.scrollTo(0, 0); }} />;
  }

  return (
    <>
      <ImpersonationBanner />
      <div style={impersonating ? { paddingTop: 36 } : undefined}>
      {/* Show menu: either no active module (for non-editors/non-RDs) or explicitly navigated to menu */}
      {!activeModule && !isRD && !isEditor && !isSocialManager && (
        <ModuleSelector onSelectModule={handleSelectModule} />
      )}
      
      {activeModule === 'script' && (
        <ScriptBuilder onBack={handleBack} onBuildPresentour={handleBuildPresentour} />
      )}
      
      {activeModule === 'social' && (
        <SocialImageBuilder onBack={handleBack} />
      )}
      
      {activeModule === 'video' && (
        <SocialVideoBuilder onBack={handleBack} />
      )}
      
      {activeModule === 'presentour' && (
        <PresentourBuilder onBack={handleBack} scriptData={presentourScriptData} />
      )}
      
      {activeModule === 'admin' && (isSuperAdmin || isClientManager) && (
        <AdminPanel onBack={handleBack} />
      )}
      
      {activeModule === 'management' && isManager && (
        <ManagerPanel onBack={handleBack} />
      )}
      
      {activeModule === 'listings' && (
        <BranchListings onBack={handleBack} onNavigate={handleSelectModule} />
      )}
      
      {activeModule === 'social-week' && (isSuperAdmin || user?.social_week_access) && (
        <SocialWeekBuilder onBack={handleBack} />
      )}

      {activeModule === 'valuation-tools' && (
        <ValuationTools onBack={handleBack} />
      )}

      {activeModule === 'social-hub' && !isSocialManager && (
        <SocialManagerPortal onBack={handleBack} />
      )}

      {activeModule === 'reels' && (
        <AgentReels onBack={handleBack} />
      )}

      {activeModule === 'property-description' && (
        <PropertyDescriptionBuilder onBack={handleBack} />
      )}

      {activeModule === 'avatar-onboarding' && (
        <AvatarOnboardingBuilder onBack={handleBack} />
      )}

      {activeModule === 'employment-intelligence' && (
        <EmploymentIntelligence onBack={handleBack} />
      )}
      
      {activeModule === 'video-search' && (
        <VideoSearch onBack={handleBack} onNavigate={handleSelectModule} />
      )}

      {activeModule === 'social-lite' && user?.role === 'super_admin' && (
        <SocialLite onBack={() => handleSelectModule('video-search')} onNavigate={handleSelectModule} />
      )}

      {activeModule === 'cost-centre' && user?.role === 'super_admin' && (
        <CostCentre onBack={handleBack} />
      )}

      {/* Editor auto-redirect: show editor panel when no module selected and user is editor (first login) */}
      {(activeModule === 'editor' || (!activeModule && isEditor)) && (
        <EditorPanel onBack={isEditor ? undefined : handleBack} onNavigate={handleSelectModule} />
      )}

      {/* Regional Director — lands straight on Proposals dashboard. No tools menu. */}
      {isRD && (
        <RegionalDirectorDashboard />
      )}

      {/* Social / Marketing Manager — lands straight on the Social Library portal */}
      {isSocialManager && (
        <SocialManagerPortal />
      )}

      {/* Super admin opens the same dashboard as a module (incl. Team Structure) */}
      {activeModule === 'proposals' && !isRD && (
        <RegionalDirectorDashboard onBack={handleBack} />
      )}

      {/* Full-screen Team experience — opened from the carousel Team tile */}
      {activeModule === 'team' && (
        <TeamPage onBack={handleBack} />
      )}
      </div>
    </>
  );
}

function App() {
  // Detect /capture route — render standalone without auth
  if (window.location.pathname === '/capture') {
    return <CaptureApp />;
  }
  // Jackson-Stops PechaKucha deck — standalone, passcode-gated, no auth context
  if (window.location.pathname === PRESENTATION_BASE || window.location.pathname.startsWith(`${PRESENTATION_BASE}/`)) {
    return <JacksonStopsPresentation />;
  }
  // Artwork export: still frame of the showcase roundabout on a transparent background
  if (window.location.pathname === '/render/carousel') {
    return <CarouselRender />;
  }
  // Detect /proposal/{slug} — public prospect-facing pitch page
  const proposalMatch = window.location.pathname.match(/^\/proposal\/([A-Za-z0-9_-]+)$/);
  if (proposalMatch) {
    return <ProposalPublicView slug={proposalMatch[1]} />;
  }
  // Detect /autocue/{slug} — public teleprompter view (loaded via QR on a phone)
  const autocueMatch = window.location.pathname.match(/^\/autocue\/([A-Za-z0-9_-]+)$/);
  if (autocueMatch) {
    return <AutocueView slug={autocueMatch[1]} />;
  }

  return (
    <AuthProvider>
      <AppContent />
      <Toaster />
    </AuthProvider>
  );
}

export default App;
